/**
 * The shared option surface, used by every option-bearing Item type.
 *
 * **A withheld option is shown, disabled and attributed — never removed from the list.** Silently
 * dropping it would leave a planner unable to tell the difference between an option that does not
 * exist and one the diocese withheld, and those call for completely different responses: one is a
 * dead end, the other is a conversation with whoever set the Policy.
 *
 * The exception that proves it: an option that is both withheld *and* currently selected is a
 * conflict. It stays visible, marked, and is never quietly cleared
 * (docs/engineering/06-selection-policy.md).
 */

import type { OptionAvailability } from '@pius/contracts';

import { attributionFor } from './types';
import './renderers.css';

export function OptionList({
  name,
  options,
  selected,
  multiple,
  disabled,
  onToggle,
}: {
  name: string;
  options: OptionAvailability[];
  selected: string[];
  multiple: boolean;
  disabled: boolean;
  onToggle: (key: string) => void;
}): React.JSX.Element {
  if (options.length === 0) {
    return <p className="ren-empty">This template version declares no options for this item.</p>;
  }

  return (
    <div className="ren-options" role={multiple ? 'group' : 'radiogroup'} aria-label={name}>
      {options.map((option) => {
        const isSelected = selected.includes(option.key);
        // A withheld option is only interactive if choosing it would clear a conflict — which it
        // never would, so it is never interactive. Deselecting is done by choosing something else.
        const locked = disabled || !option.offered;
        const attribution = attributionFor(option);

        return (
          <label
            key={option.key}
            className={[
              'ren-option',
              !option.offered ? 'ren-option--withheld' : '',
              isSelected && !option.offered ? 'ren-option--conflict' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <input
              type={multiple ? 'checkbox' : 'radio'}
              name={name}
              value={option.key}
              checked={isSelected}
              disabled={locked}
              onChange={() => onToggle(option.key)}
            />
            <span className="ren-option__label">{option.label}</span>

            {isSelected && !option.offered ? (
              <span className="ren-option__conflict">
                Approved, no longer offered — an administrator decides what happens next
              </span>
            ) : attribution ? (
              <span className="ren-option__source">{attribution}</span>
            ) : null}
          </label>
        );
      })}
    </div>
  );
}
